import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronRight, RotateCw, ThumbsUp, ThumbsDown, Flame } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useSpacedRepetitionScheduler } from "@/hooks/useSpacedRepetitionScheduler";
import { useGamification } from "@/hooks/useGamification";
import type { Flashcard } from "@/types/flashcard";
import { transformDatabaseFlashcard } from "@/lib/flashcard-utils";
import FlashcardView from './FlashcardView';

interface FlashcardReviewProps {
  deckId?: string;
  showMistakesOnly?: boolean;
}

const FlashcardReview: React.FC<FlashcardReviewProps> = ({ deckId, showMistakesOnly = false }) => {
  const [flashcards, setFlashcards] = useState<Flashcard[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [reviewCompleted, setReviewCompleted] = useState(false);
  const [reviewedCount, setReviewedCount] = useState(0);
  const { scheduleNextReview, getDueFlashcards, getMistakeFlashcards } = useSpacedRepetitionScheduler();
  const { markFlashcardProgress } = useGamification();

  useEffect(() => {
    console.log('FlashcardReview Component Mounted', {deckId, showMistakesOnly});
    loadFlashcards();
  }, [deckId, showMistakesOnly]);

  const loadFlashcards = async () => {
    setIsLoading(true);
    try {
      console.log('Loading flashcards start');
      console.log('Current configuration:', { deckId, showMistakesOnly });
      
      let cards: Flashcard[] = [];
      
      if (showMistakesOnly) {
        console.log('Loading flashcards from quiz mistakes');
        if (deckId) {
          console.log('Loading specific mistake deck with ID:', deckId);
          
          const { data: deckData, error: deckError } = await supabase
            .from('flashcard_decks')
            .select('name')
            .eq('id', deckId)
            .maybeSingle();
            
          if (deckError) {
            console.error('Error verifying deck:', deckError);
            throw deckError;
          }
          
          const isMistakeDeck = deckData?.name?.includes('Quiz Mistakes');
          console.log(`Deck ${deckId} is ${isMistakeDeck ? 'a' : 'not a'} mistake deck. Name: ${deckData?.name}`);
          
          const { data, error } = await supabase
            .from('flashcards')
            .select('*')
            .eq('deck_id', deckId);
            
          if (error) {
            console.error('Error fetching specific deck cards:', error);
            throw error;
          }
          
          if (!data || data.length === 0) {
            console.log('No cards found in the specified deck.');
          } else {
            cards = data.map(card => transformDatabaseFlashcard(card));
            console.log(`Found ${cards.length} cards in specified deck`);
          }
        } else {
          console.log('Loading all mistake cards across decks');
          
          const { data: sourceCards, error: sourceError } = await supabase
            .from('flashcards')
            .select('*')
            .eq('source', 'quiz_mistake');
          
          if (sourceError) {
            console.error('Error fetching source-tagged cards:', sourceError);
          } else if (sourceCards && sourceCards.length > 0) {
            cards = sourceCards.map(card => transformDatabaseFlashcard(card));
            console.log(`Found ${cards.length} cards with source='quiz_mistake'`);
          } else {
            const mistakeCards = await getMistakeFlashcards();
            if (mistakeCards.length > 0) {
              cards = mistakeCards;
              console.log(`Found ${cards.length} cards from mistake decks`);
            } else {
              console.log('No quiz mistake cards found by either method');
            }
          }
        }
      } else if (deckId) {
        console.log('Loading all cards in the selected deck');
        const { data, error } = await supabase
          .from('flashcards')
          .select('*')
          .eq('deck_id', deckId);
          
        if (error) {
          console.error('Error fetching deck cards:', error);
          throw error;
        }
        
        cards = data?.map(card => transformDatabaseFlashcard(card)) || [];
        
        console.log(`Found ${cards.length} cards in deck: ${deckId}`);
      } else {
        console.log('Loading due cards across all decks');
        cards = await getDueFlashcards();
        console.log(`Found ${cards.length} due cards across all decks`);
      }
      
      // Shuffle cards for a randomized review session
      const shuffled = [...cards].sort(() => Math.random() - 0.5);
      
      console.log(`Setting ${shuffled.length} shuffled flashcards for review`);
      setFlashcards(shuffled);
      setCurrentIndex(0);
      setReviewCompleted(shuffled.length === 0);
      console.log('Flashcards loaded successfully');
    } catch (error) {
      console.error('Error in loadFlashcards:', error);
      toast.error('Failed to load flashcards');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfidenceRating = async (confidence: number) => {
    if (currentIndex >= flashcards.length) return;
    
    const currentCard = flashcards[currentIndex];
    if (!currentCard?.id) {
      console.error('Current card is invalid:', currentCard);
      toast.error('Error processing this flashcard');
      return;
    }
    
    try {
      console.log(`Recording confidence ${confidence} for card ${currentCard.id}`);
      await scheduleNextReview(currentCard.id, confidence);
      setReviewedCount(prev => prev + 1);
      
      if (currentIndex === flashcards.length - 1) {
        console.log('Review completed, all cards reviewed');
        setReviewCompleted(true);
        await markFlashcardProgress(reviewedCount + 1);
        toast.success(`Completed review of ${reviewedCount + 1} flashcards!`);
        return;
      }
    } catch (error) {
      console.error('Error updating flashcard:', error);
      toast.error('Failed to update flashcard progress');
    }
  };

  const handleNext = () => {
    if (currentIndex < flashcards.length - 1) {
      setCurrentIndex(prevIndex => prevIndex + 1);
    } else {
      setReviewCompleted(true);
      markFlashcardProgress(reviewedCount + 1)
        .catch(error => console.error('Error marking flashcard progress:', error));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading flashcards...</span>
      </div>
    );
  }

  if (reviewCompleted) {
    return (
      <div className="text-center p-8">
        <Flame className="h-12 w-12 mx-auto mb-4 text-orange-500" />
        <h2 className="text-2xl font-bold mb-4">Review Complete!</h2>
        <p className="mb-6 text-gray-600">
          You've reviewed all {reviewedCount} {showMistakesOnly ? "mistake " : ""}flashcards.
        </p>
        <Button onClick={loadFlashcards} className="mx-auto">
          <RotateCw className="mr-2 h-4 w-4" />
          Check Again
        </Button>
      </div>
    );
  }

  if (!flashcards || flashcards.length === 0) {
    return (
      <div className="text-center p-8">
        <h2 className="text-xl font-bold mb-4">
          {showMistakesOnly 
            ? "No Mistake Flashcards Found" 
            : "No Flashcards Due for Review"}
        </h2>
        <p className="mb-6 text-gray-600">
          {showMistakesOnly 
            ? "Complete some quizzes to generate flashcards from your mistakes." 
            : "There are no flashcards due for review at this time."}
        </p>
        <Button onClick={loadFlashcards} className="mx-auto">
          <RotateCw className="mr-2 h-4 w-4" />
          Check Again
        </Button>
      </div>
    );
  }

  const currentCard = flashcards[currentIndex];
  if (!currentCard) {
    return (
      <div className="text-center p-8">
        <h2 className="text-xl font-bold mb-4">Error Loading Flashcard</h2>
        <p className="mb-6 text-gray-600">Unable to load the current flashcard. Please try again.</p>
        <Button onClick={loadFlashcards} className="mx-auto">
          <RotateCw className="mr-2 h-4 w-4" />
          Reload Flashcards
        </Button>
      </div>
    );
  }

  // Use the FlashcardView component for displaying cards
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Card {currentIndex + 1} of {flashcards.length}
        </div>
        <div className="text-sm font-medium px-2 py-1 bg-gray-100 rounded-full">
          {currentCard.difficulty || 'medium'}
        </div>
      </div>

      <FlashcardView 
        card={currentCard}
        onConfidenceRating={handleConfidenceRating}
        onNext={handleNext}
      />
    </div>
  );
};

export default FlashcardReview;
